import React, { useEffect, Suspense, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AudioThemeProvider } from './theme/ThemeProvider';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { ClientShellGuard } from './components/common/ClientShellGuard';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { StateSyncService } from './services/StateSyncService';
import { pageCleanupService } from './services/pageCleanupService';
import { useAuthStore } from './stores/authStore';
import { runSessionMigration } from './services/sessionMigration';
import { authService } from './services/authService';
import { sessionHeartbeatService } from './services/sessionHeartbeatService';
import { i18nService } from './i18n/i18nService';
import TestPage from './TestPage'; // 导入测试页面
import './utils/testApiConnection'; // 导入API测试工具
import './styles/animations.css'; // 导入全局动画样式

// 懒加载主应用组件以优化初始加载
const MobileApp = lazy(() => import('./components/MobileApp/MobileApp').then(module => ({ default: module.MobileApp })));
const UserProfile = lazy(() => import('./components/UserProfile/UserProfile').then(module => ({ default: module.UserProfile })));
const TopBarPage = lazy(() => import('./components/AmplifierControl/Index').then(module => ({ default: module.TopBar })));
const RadioRemotePage = lazy(() =>
  import('./pages/radioRemote/RadioRemotePage').then((module) => ({ default: module.RadioRemotePage }))
);

// 加载骨架屏组件
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    flexDirection: 'column',
    gap: '16px',
    color: 'var(--color-text-secondary)'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '3px solid var(--color-border-primary)',
      borderTopColor: 'var(--color-primary)',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
    <p>加载中...</p>
    <style>{`
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* 公开测试路由 */}
        <Route path="/test" element={
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="page-enter"
          >
            <TestPage />
          </motion.div>
        } />
        {/* 受保护路由 */}
        <Route path="/" element={
          <ProtectedRoute>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="page-enter"
            >
              <MobileApp />
            </motion.div>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3 }}
              className="page-enter"
            >
              <UserProfile />
            </motion.div>
          </ProtectedRoute>
        } />
        <Route path="/amplifier-control" element={
          <ProtectedRoute>
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3 }}
              className="page-enter"
            >
              <TopBarPage
                isConnected={true}
                isMcuConnected={true}
                onAntennaChange={() => {}}
                onTriggerModeChange={() => {}}
                onLanguageChange={(lang) => {
                  i18nService.setLanguage(lang);
                  console.log('语言切换为:', lang);
                }}
              />
            </motion.div>
          </ProtectedRoute>
        } />
        <Route path="/audio-control" element={
          <ProtectedRoute>
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3 }}
              className="page-enter"
            >
              <MobileApp />
            </motion.div>
          </ProtectedRoute>
        } />
        <Route path="/radio-remote" element={
          <ProtectedRoute>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="page-enter"
            >
              <Suspense fallback={<LoadingFallback />}>
                <RadioRemotePage />
              </Suspense>
            </motion.div>
          </ProtectedRoute>
        } />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  useEffect(() => {
    const migrationResult = runSessionMigration();
    if (migrationResult.migrated) {
      console.warn('Access 会话模型迁移:', migrationResult.message);
    }

    useAuthStore.getState().initializeAuth();

    const isDevelopment = process.env.NODE_ENV === 'development';

    const checkAndCleanupLocalStorage = () => {
      try {
        const token = authService.getToken();
        const userStr = localStorage.getItem('user');

        if (token && (!userStr || !JSON.parse(userStr))) {
          console.warn('检测到无效的用户信息，清除本地存储');
          authService.clearAuth();
        }
      } catch (error) {
        console.error('本地存储检查失败:', error);
        authService.clearAuth();
      }
    };

    // 初始化状态同步服务
    const stateSyncService = StateSyncService.getInstance();

    // 开发环境下，先清理可能存在的旧状态
    if (isDevelopment) {
      console.log('开发环境：清理可能存在的旧状态');
      stateSyncService.dispose();
    }

    const initializeServices = async () => {
      try {
        // 先检查本地存储
        checkAndCleanupLocalStorage();

        // 初始化页面清理服务
        pageCleanupService.initialize();

        await stateSyncService.initialize();

        // 如果用户已经登录，初始化需要认证的服务
        const token = authService.getToken();
        if (token) {
          console.log('检测到已登录用户，初始化需要认证的服务');
          sessionHeartbeatService.start();
          await stateSyncService.initializeAuthenticatedServices();
        }
      } catch (error) {
        console.error('服务初始化失败:', error);
      }
    };

    // 监听用户登录成功事件
    const handleAuthSuccess = async () => {
      console.log('用户登录成功，初始化需要认证的服务');
      try {
        sessionHeartbeatService.start();
        await stateSyncService.initializeAuthenticatedServices();
      } catch (error) {
        console.error('认证服务初始化失败:', error);
      }
    };

    const handleAuthLogout = () => {
      console.log('用户登出，清理状态同步服务');
      sessionHeartbeatService.stop();
      stateSyncService.dispose();
    };

    initializeServices();

    // 添加事件监听器
    window.addEventListener('auth-success', handleAuthSuccess);
    window.addEventListener('auth-logout', handleAuthLogout);

    // 清理函数
    return () => {
      // 移除事件监听器
      window.removeEventListener('auth-success', handleAuthSuccess);
      window.removeEventListener('auth-logout', handleAuthLogout);

      stateSyncService.dispose();
      pageCleanupService.destroy();
    };
  }, []);

  return (
    <ErrorBoundary>
      <AudioThemeProvider>
        <Router>
          <ClientShellGuard />
          <Suspense fallback={<LoadingFallback />}>
            <AnimatedRoutes />
          </Suspense>
        </Router>
      </AudioThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
